// KitchenReferenceModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import AutocompleteSelect from '../../../cabinet/wizplain/components/Autocomplete/AutocompleteSelect';
import useProductAutocomplete from '../../../../components/ProductAutocomplete/useProductAutocomplete';
import MediaUploader from '../../../../components/ReferenceMedia/MediaUploader';

const KitchenReferenceModal = ({
  isOpen,
  onClose,
  referenceType,
  config,
  itemData,
  onSubmit,
  viewMode = false,
  isEditPossible = true,
  loading = false
}) => {
  const [formData, setFormData] = useState({});

  // Base-product picker for the products dictionary's baseProductId field — server-side search
  // (see reference.search-pagination.patch.md), so this scales to hundreds/thousands of products
  // instead of loading the whole dictionary into a <select>.
  const baseProductSearch = useProductAutocomplete({ referenceType: 'products' });

  useEffect(() => {
    if (isOpen && referenceType === 'product-items') baseProductSearch.loadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, referenceType]);

  // Статические опции для разных справочников
  const staticOptions = {
    products: {
      categories: ['Овочі', 'Фрукти', "М'ясо", 'Риба', 'Молочні продукти', 'Крупи', 'Бобові', 'Хлібобулочні вироби', 'Напої', 'Інше'],
      units: ['кг', 'г', 'л', 'мл', 'шт']
    },
    dishes: {
      categories: ['Перші страви', 'Другі страви', 'Гарніри', 'Салати', 'Десерти', 'Напої'],
      complexity: ['Легка', 'Середня', 'Складна'],
      ageGroups: ['1-3 роки', '3-6 років', '6-10 років']
    },
    'nutrition-norms': {
      ageGroups: ['1-3 роки', '3-6 років', '6-10 років', '10-14 років']
    }
  };

  useEffect(() => {
    if (itemData) {
      setFormData(itemData);
    } else {
      setFormData({});
    }
  }, [itemData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const renderField = (field) => {
    const { name, type, label, required } = field;
    const value = formData[name] || '';

    const commonProps = {
      name,
      value,
      onChange: handleChange,
      required: required && !viewMode,
      disabled: viewMode || !isEditPossible || loading,
      readOnly: viewMode
    };

    switch (type) {
      case 'text':
      case 'textarea':
        return (
          <Form.Control
            as="textarea"
            rows={3}
            placeholder={`Введіть ${label.toLowerCase()}`}
            {...commonProps}
          />
        );

      case 'number':
        return (
          <Form.Control
            type="number"
            step={name.includes('proteins') || name.includes('fats') || name.includes('carbohydrates') ? '0.1' : '1'}
            placeholder={`Введіть ${label.toLowerCase()}`}
            {...commonProps}
          />
        );

      case 'array':
        const options = staticOptions[referenceType]?.[name] || [];
        return (
          <Form.Select {...commonProps} multiple>
            {options.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        );

      case 'productRef': {
        // Base-product picker: searches the separate 'products' (base) dictionary — trade items
        // never appear here since that's a different referenceType entirely now. The item being
        // edited itself is excluded so a base product can't accidentally reference itself
        // (relevant if this field type is ever reused on the 'products' config too).
        const currentId = itemData?._id;
        const filteredOptions = baseProductSearch.options.filter(opt => opt._id !== currentId);

        return (
          <AutocompleteSelect
            value={value}
            onChange={(id) => setFormData(prev => ({ ...prev, [name]: id }))}
            options={filteredOptions}
            loading={baseProductSearch.loading}
            loadingMore={baseProductSearch.loadingMore}
            hasMore={baseProductSearch.hasMore}
            onSearch={baseProductSearch.search}
            onLoadMore={baseProductSearch.loadMore}
            placeholder="— це базовий продукт (без прив'язки), або почніть вводити назву —"
            disabled={commonProps.disabled}
          />
        );
      }

      default:
        // Для enum полей используем select
        if (staticOptions[referenceType]?.[name]) {
          const options = staticOptions[referenceType][name];
          return (
            <Form.Select {...commonProps}>
              <option value="">Оберіть {label.toLowerCase()}</option>
              {options.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          );
        }

        return (
          <Form.Control
            type="text"
            placeholder={`Введіть ${label.toLowerCase()}`}
            {...commonProps}
          />
        );
    }
  };

  const getModalTitle = () => {
    const itemName = itemData?.name || itemData?.ageGroup || 'запис';
    if (viewMode) return `Перегляд ${itemName}`;
    return itemData ? `Редагувати ${itemName}` : `Додати новий запис`;
  };

  if (!config) {
    return (
      <Modal show={isOpen} onHide={onClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Завантаження...</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Завантаження форми...</p>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{getModalTitle()}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            {config.fields?.map((field, index) => (
              <Col key={field.name} md={field.type === 'textarea' ? 12 : 6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {field.label}
                    {field.required && !viewMode && ' *'}
                  </Form.Label>
                  {renderField(field)}
                </Form.Group>
              </Col>
            ))}
          </Row>

          {referenceType === 'product-items' && (
            <Form.Group className="mb-3">
              <Form.Label>Фото</Form.Label>
              <MediaUploader
                target="product-item"
                targetId={itemData?._id}
                itemName={itemData?.name || formData.name}
                maxCount={1}
                disabled={viewMode || !isEditPossible}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {viewMode ? 'Закрити' : 'Скасувати'}
          </Button>
          {!viewMode && isEditPossible && (
            <Button variant="primary" type="submit" disabled={loading}>
              {loading && <Spinner animation="border" size="sm" className="me-2" />}
              {itemData ? 'Зберегти зміни' : 'Додати запис'}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default KitchenReferenceModal;